import { StaffApiSchema } from "@apiUtils/index";
import { LoadingSkeleton, Row } from "@narmi/design_system";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { searchResultKey, searchResultPath } from "./searchNavigation";
import styles from "./SearchPreview.module.css";

type SettingsSearchResult = StaffApiSchema.components["schemas"]["SettingsSearchResult"];

const categoryIcon: Record<string, string | undefined> = {
  "All products": undefined,
  General: "narmi-icon-tool",
  Banking: "narmi-icon-bank",
  Grow: "narmi-icon-book-open",
  Lending: "narmi-icon-loan",
};

export function SearchPreview({
  searchResults,
  totalResultCount,
  isSearchLoading,
  selectedCategory,
  institutionCategoriesWithCount,
  onSelectSearchCategory,
  onSelectSearchResult,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  searchResults: SettingsSearchResult[];
  totalResultCount?: number;
  isSearchLoading: boolean;
  selectedCategory?: string;
  institutionCategoriesWithCount: { name: string; setting_count: number }[];
  onSelectSearchCategory: (category: string | undefined) => void;
  onSelectSearchResult: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll: fetch the next page once the sentinel at the bottom of the
  // result list scrolls into the preview's own viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root, rootMargin: "120px" }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore, searchResults.length]);

  return (
    <div className={styles.searchPreview}>
      {isSearchLoading ? (
        <LoadingSkeleton isLoading={true} />
      ) : (
        <>
          <Row gapSize="s" alignItems="center" className="padding--all--l">
            <CategoryButton
              label="All products"
              settingCount={institutionCategoriesWithCount.reduce(
                (acc, category) => acc + category.setting_count,
                0
              )}
              isSelected={selectedCategory === undefined}
              onClick={() => onSelectSearchCategory(undefined)}
            />
            {institutionCategoriesWithCount.map((category) => (
              <CategoryButton
                key={category.name}
                label={category.name}
                settingCount={category.setting_count}
                isSelected={selectedCategory === category.name}
                onClick={() => onSelectSearchCategory(category.name)}
              />
            ))}
          </Row>
          <div className={styles.searchResultsScrollContainer} ref={scrollContainerRef}>
            <div className={styles.searchResultsContainer}>
              {searchResults.map((result) => (
                <SearchResultItem
                  key={searchResultKey(result)}
                  result={result}
                  onSelect={onSelectSearchResult}
                />
              ))}
              {searchResults.length === 0 && (
                <div className="fontSize--s fontColor--secondary padding--y--l">
                  No matching settings.
                </div>
              )}
              <div ref={sentinelRef} className={styles.scrollSentinel} aria-hidden="true" />
              {isFetchingNextPage && (
                <div className={styles.loadingMore}>
                  <LoadingSkeleton isLoading={true} />
                </div>
              )}
            </div>
          </div>
          {totalResultCount !== undefined && (
            <div className={styles.searchResultsFooter}>
              Showing {searchResults.length} of {totalResultCount} results
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CategoryButton = ({
  isSelected,
  label,
  settingCount,
  onClick,
}: {
  isSelected: boolean;
  label: string;
  settingCount: number;
  onClick: () => void;
}) => (
  <Row.Item shrink>
    <button
      type="button"
      className={`${styles.categoryButton} ${isSelected ? styles.selected : ""}`}
      aria-pressed={isSelected}
      onClick={onClick}
    >
      {categoryIcon[label] && <span className={categoryIcon[label]} aria-hidden="true" />}
      <span className={`fontSize--s ${!isSelected ? "fontColor--secondary" : ""}`}>{label}</span>
      <span className={styles.countBox}>{settingCount}</span>
    </button>
  </Row.Item>
);

const SearchResultItem = ({
  result,
  onSelect,
}: {
  result: SettingsSearchResult;
  onSelect: () => void;
}) => {
  return (
    <Link to={searchResultPath(result)} onClick={onSelect} className={styles.searchResultItem}>
      <Row>
        <Row.Item shrink>
          <span
            className={[categoryIcon[result.category], styles.searchResultIcon]
              .filter(Boolean)
              .join(" ")}
            aria-hidden="true"
          />
        </Row.Item>
        <Row.Item>
          <div className={styles.searchResultItemDetails}>
            <SearchResultBreadCrumb result={result} />
            <div className="fontSize--m">{result.human_facing_name}</div>
            <div className="fontSize--s fontColor--secondary">{result.help_text}</div>
          </div>
        </Row.Item>
      </Row>
    </Link>
  );
};

const SearchResultBreadCrumb = ({ result }: { result: SettingsSearchResult }) => {
  return (
    <div className={styles.searchResultBreadcrumb}>
      <span>{result.category}</span>
      <span className="narmi-icon-chevron-right" aria-hidden="true"></span>
      <span>{result.group}</span>
      <span className="narmi-icon-chevron-right" aria-hidden="true"></span>
      {result.result_type === "group" ? (
        <>
          <span>
            ({result.result_type}) {result.setting_count} settings
          </span>
        </>
      ) : result.result_type === "subgroup" ? (
        <>
          <span>{result.human_facing_name}</span>
          <span className="narmi-icon-chevron-right" aria-hidden="true"></span>
          <span>
            ({result.result_type}) {result.setting_count} settings
          </span>
        </>
      ) : (
        <>
          <span>
            ({result.result_type}) {result.subgroup}
          </span>
        </>
      )}
    </div>
  );
};
